import JSONModel from "sap/ui/model/json/JSONModel";
import Dialog from "sap/m/Dialog";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import V4Context from "sap/ui/model/odata/v4/Context";
import { Article, Product, RequestPosition } from "vamos/cockpit/model/odata/VamosModel";
import Field, { Field$ChangeEvent } from "sap/ui/mdc/Field";
import ValueHelp from "sap/ui/mdc/ValueHelp";
import BaseController from "vamos/cockpit/base/controller/type/BaseController";
import formatter from "vamos/cockpit/base/formatter/formatter";
import JsonUtil from "vamos/cockpit/base/util/JsonUtil";
import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import ValidationUtil from "vamos/cockpit/base/util/ValidationUtil";
import { showSuccess } from "vamos/cockpit/base/handler/MessageToastHandler";
import ProductService from "vamos/cockpit/data/product/service/ProductService";
import Input from "sap/m/Input";


/**
 * @namespace vamos.cockpit.data.request.controller
 */
export default class RequestPositionDialogController extends BaseController {
    public formatter: formatter = formatter;

    private dialog: Dialog;
    private dataModel: JSONModel;
    private context: V4Context;
    private productService: ProductService;

    private articleField: Field;
    private unitInput: Input;
    private variantField: Field;
    private requestGuid: string;
    private changed: boolean = false;
    private saveCallBack: () => void;

    onInit(): void {
        super.onInit();
        this.productService = new ProductService(this.getComponent());
        this.dialog = this.getView().byId("requestPositionDialog") as Dialog;
        this.articleField = this.getView().byId("articleField") as Field;
        this.variantField = this.getView().byId("variantField") as Field;
        this.unitInput = this.getView().byId("unitInput") as Input;
        this.dataModel = new JSONModel({});
        this.dialog.setModel(this.dataModel, "data");
    }

    public openDialog(requestPositionContext: V4Context, requestGuid: string, saveCallBack?: () => void): void {
        this.resetData();
        this.context = requestPositionContext;
        this.requestGuid = requestGuid;
        this.changed = false;
        this.saveCallBack = saveCallBack;
        const position: RequestPosition | null = requestPositionContext?.getObject() as RequestPosition;
        this.setRequestPositionData(position);

        const articleGuid = this.dataModel.getProperty("/requestPosition/ArticleGuid") as string;
        this.setProductFieldFilter(articleGuid);
        if(!articleGuid){
            this.dialog.setInitialFocus(this.articleField);

        }

        this.dialog.open();
    }

    handleAfterClose() {
        if (this.changed) {
            this.saveCallBack?.();
        }
    }

    public onClosePopup(): void {
        this.dialog.close();
    }

    public async onArticleSelect(event: Field$ChangeEvent) {
        if (!event.getParameter("valid")) {
            return;
        }
        const articleField = event.getSource();
        this.setProductData(null, null);
        const articleGuid:string = event.getParameter("value")
        const filter = this.setProductFieldFilter(articleGuid);
        const product = await this.productService.checkCountAndReturnSingleProduct(filter);
        if(product) {
            this.setUnitData(product);
            this.setProductData(product.Guid, product.Name);
            this.focusUnitField();
        }
        const oVH = this.byId(articleField.getValueHelp()) as ValueHelp;
        const delegate = await oVH.awaitControlDelegate();
        const articleContext = delegate.getSelectedContext(oVH);
        const article = articleContext.getObject() as Article;

        this.dataModel.setProperty("/requestPosition/ArticleNumber", article.Number);
        this.dataModel.setProperty("/requestPosition/ArticleName", article.Name);



    }
    public onPositionSaveAndNew() {
        this.articleField.setAdditionalValue(null);
    }

    private setProductData(productGuid:string, variantName:string) {
        this.dataModel.setProperty("/requestPosition/ProductGuid", productGuid);
        this.dataModel.setProperty("/requestPosition/VariantName", variantName);
    }

    private setProductFieldFilter(articleGuid: string):Filter {
        if (!articleGuid) return;

        const valueHelp = this.byId(this.variantField.getValueHelp()) as ValueHelp;
        const filter:Filter = new Filter("ArticleGuid", FilterOperator.EQ, articleGuid);
        valueHelp._oCustomFilter = filter;

        return filter;
    }

    public onProductSelect(event: Field$ChangeEvent) {

        if (!event.getParameter("valid")) {
            return;
        }
        try {
            const id = this.variantField.getValueHelp();
            const vh = this.byId(id) as ValueHelp;
            const delegate = vh.getControlDelegate() as any;
            const variantContext = delegate.getSelectedContext(vh);
            const product: Product = variantContext.getObject() as Product;
            this.setUnitData(product);



        } catch (e) {
            console.error(e);
        }
    }

    public async onSave() {
        if (ValidationUtil.hasEmptyRequiredFields(this.dialog)) {
            return;
        }
        await this.save();
        this.onClosePopup();
    }

    private async save() {
        if (!this.context) {
            const model = this.getModel() as ODataModel;
            this.context = this.createModelEntry("RequestPosition", { RequestGuid: this.requestGuid }, null, model.getUpdateGroupId()) as V4Context;
        }
        JsonUtil.json2OData(this.context, this.getRequestPositionData(), "changes");
        try {
            await this.submitBatch(this.context.getUpdateGroupId());
            showSuccess(this.getText("EntrySaved"));
            this.changed = true;
        } catch (e) {
            console.error(e);
        }
    }

    private async onSaveAndNew() {
        if (ValidationUtil.hasEmptyRequiredFields(this.dialog)) {
            return;
        }

        await this.save();
        this.resetData();


    }
    private setUnitData(product: Product) {
        if (product.BaseAmount !== this.dataModel.getProperty("/requestPosition/BaseAmount") || product.BaseUnitName !== this.dataModel.getProperty("/requestPosition/BaseUnitName")) {
            this.dataModel.setProperty("/requestPosition/Amount", 0.00);
        }
        this.dataModel.setProperty("/requestPosition/BaseAmount", product.BaseAmount);
        this.dataModel.setProperty("/requestPosition/BaseUnitName", product.BaseUnitName);
    }
    private setRequestPositionData(requestPosition: RequestPosition) {
        this.dataModel.setProperty("/requestPosition", requestPosition ? requestPosition : {});
    }

    private getRequestPositionData(): RequestPosition {
        return this.dataModel.getProperty("/requestPosition") as RequestPosition;
    }


    private resetData() {
        this.context = null;
        ValidationUtil.clearRequiredFields(this.dialog);
        this.setArticleValueHelpFilter();
        this.setRequestPositionData(null);
    }
    private setArticleValueHelpFilter() {
        const oValueHelp = this.byId("article-vh");
        const oFilter = new Filter({
            path: "ProductDetails",
            operator: FilterOperator.Any,
        });
        oValueHelp._oCustomFilter = [oFilter];
    }
    private focusUnitField() {
        this.unitInput.focus();
    }

}